import { memo } from 'react';
import styled from 'styled-components';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { P, fs, ease, tint, tintT } from '../theme/tokens';
import { formatPhone } from '../utils/callLog';
import { relativeWhen } from '../utils/datetime';
import Avatar from './Avatar';
import MetaChip from './MetaChip';

const Row = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  padding: 13px 16px;
  border: none;
  border-bottom: 1px solid ${P.line};
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  background: ${(p) => (p.$selected ? P.rowSel : P.white)};
  box-shadow: inset 3px 0 0 ${(p) => (p.$selected ? P.navy : 'transparent')};
  transition: background 150ms ${ease}, box-shadow 150ms ${ease};

  &:hover {
    background: ${(p) => (p.$selected ? P.rowSel : P.rowHover)};
  }
  &:focus-visible {
    outline: 2px solid ${P.navy};
    outline-offset: -2px;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const Name = styled.span`
  min-width: 0;
  font-size: ${fs.sm}px;
  font-weight: ${(p) => (p.$unread ? 700 : 600)};
  color: ${P.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const When = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${(p) => (p.$unread ? P.info : P.subtle)};
  font-weight: ${(p) => (p.$unread ? 700 : 400)};
  white-space: nowrap;
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${P.info};
`;

const Preview = styled.div`
  margin-top: 3px;
  font-size: ${fs.xs}px;
  color: ${(p) => (p.$unread ? P.ink : P.subtle)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 7px;
`;

function ConversationRow({ conversation: c, role, selected, onSelect, tagColor }) {
  const unread = c.unread > 0;
  const title = c.name || formatPhone(c.contactNumber);

  return (
    <Row type="button" $selected={selected} aria-current={selected || undefined} onClick={() => onSelect(c.contactNumber)}>
      <Avatar name={c.name} icon={c.name ? null : faUser} size={38} color={tint(role.color)} text={tintT(role.color)} />
      <div>
        <Top>
          <Name $unread={unread}>{title}</Name>
          <When $unread={unread}>
            {relativeWhen(c.lastAt)}
            {unread && <Dot aria-label={`${c.unread} unread`} />}
          </When>
        </Top>
        <Preview $unread={unread}>
          {c.lastDirection === 'outbound' ? 'You: ' : ''}
          {c.lastMessage || 'No messages yet'}
        </Preview>
        {c.tags?.length > 0 && (
          <Tags>
            {c.tags.map((tag) => (
              <MetaChip key={tag} color={tagColor(tag)}>
                {tag}
              </MetaChip>
            ))}
          </Tags>
        )}
      </div>
    </Row>
  );
}

export default memo(ConversationRow);
